<script setup lang="ts">
import { FocusProps, useFocusComposable } from '@components/form/compsables/focus-composable.js';
import InputLabel from '@src/application/input-label.vue';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import FormError from '@components/form/input-base-components/form-error.vue';
import { BordersProps, getBordersComposable } from '@components/form/compsables/borders-composable.js';
import {
    ModelValueEmitsType,
    ModelValuePropsInterface,
    useModelValueComposable,
} from '@components/form/compsables/model-value-composable.js';
import { Form } from '@src/form/composables/form.js';
import {
    DisabledPropsInterface,
    useDisableComposable,
} from '@src/application/composables/disabled-composable.js';
import Button from '@components/buttons/button.vue';
import { trim } from 'lodash';
import { sleep } from '@utils/sleep-util.js';
import { useSingleRefComposable } from '@src/application/composables/single-ref-composable.js';
import HorizontalAlignChildrenWrapper from '@components/layout-wrapper/horizontal-align-children-wrapper.vue';
import { v4 } from 'uuid';

const { disabled } = useDisableComposable();

interface Props extends BordersProps, FocusProps, ModelValuePropsInterface<string|null>, DisabledPropsInterface {
    eager?: boolean,
    label?: string|false,
    noLabelTopGap?: boolean,
    submitButton?: string,
    placeholder?: string,
    type?: string,
    autocomplete?: boolean,
    name?: string,
    hidden?: boolean,
    scanner?: boolean,
    inputMode?: 'text' | 'search' | 'email' | 'url' | 'tel' | 'none' | 'numeric' | 'decimal',
    validationMessage?: string|null,
    large?: boolean,
    minimizeInput?: boolean,
    form?: Form,
    onSubmit?: () => void,
    whiteFill?: boolean,
    whiteTextColor?: boolean,
    tabindex?: number,
    centerValue?: boolean,
    invisibleDisabledMode?: boolean,
    setNoInitialValue?: boolean,
    resetInputAfterSubmit?: boolean,

    // Der Placeholder sorgt dafür, dass Inputs aufgedrück werden, wenn das Value länger wird, als der Platz, den das Input zur Verfügung hat.
    // Besonders wichtig ist das für das PIM, weil dort die Inputs anhand des Values dynamisch wachsen müssen, damit die Spalten breiter werden und die Eingabe nicht versteckt angezeigt wird (overflow).
    // Auch bei Modalen (mit Forms) ist das nützlich, da diese dynamisch wachsen können.
    disableInputGrowingByHiddenPlaceholder?: boolean,

    // das hier ist ein Fix, da im Firefox bei gleichen Werten kein Change emitiert wird. Mit dieser Lösung blende ich den Input innerhalt eines Ticks aus und damit setzt der das zurück. Firefox cached anscheinend die Werte im Input WTF
    forceChangeOnSameValue?: boolean
}

const props = defineProps<Props>();

const emit = defineEmits<ModelValueEmitsType<string|null> & {
    (event: 'blur'): void,
    (event: 'focus'): void,
}>();
const { modelValue, exposes: modelValueExposes } = useModelValueComposable<string|null>();

function onChange(event: InputEvent) {
    let value: string|null = (event.target as HTMLInputElement).value;
    if (value === '') {
        value = null;
    }
    if (value !== null) {
        value = trim(value);
    }
    modelValue.value = value;
}

let event = 'change';
if (props.eager) {
    event = 'input';
}

const formName = computed(() => {
    if (props.form) {
        return props.form.uuid;
    }

    return v4();
});

let createForm = true;
if (props.form) {
    if (props.form.formElementCreated) {
        createForm = false;
    } else {
        props.form.formElementCreated = true;
    }
}

const hideInput = ref<boolean>(false);

async function onSubmit(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (!props.onSubmit) {
        return;
    }

    if (props.forceChangeOnSameValue) {
        hideInput.value = true;
        await nextTick();
        hideInput.value = false;
    }

    try {
        await props.onSubmit();
    } finally {
        if (props.scanner) {
            selectText();
        }
        if (props.resetInputAfterSubmit) {
            modelValue.value = null;
        }
    }
}

const { setSingleTemplateRef: setInputRef, singleRef: inputRef } = useSingleRefComposable();

function selectText() {
    if (inputRef.value === null) {
        return;
    }
    inputRef.value.select();
}

const { exposes, focus, blur, isFocused } = useFocusComposable(inputRef);
defineExpose({ ...exposes, ...modelValueExposes, selectText });

const clicked = ref<boolean>(false);
onMounted(() => {
    if (!props.scanner) {
        return;
    }
    focus();
});

const resetInputMode = ref(false);
const inputMode = computed<'text' | 'search' | 'email' | 'url' | 'tel' | 'none' | 'numeric' | 'decimal'>(() => {
    if (props.scanner && !clicked.value && !resetInputMode.value) {
        return 'none';
    }

    return props.inputMode ?? 'text';
});
// Der watcher setzt inputmode none nach dem nächsten Tick sofort zurück, weil chrome sonst KEINE Eingabe durch Scanner annimmt
watch(inputMode, async () => {
    await sleep(10);
    resetInputMode.value = true;
}, { immediate: true });

const { bordersConfig, focusBordersConfig } = getBordersComposable();
const showValidationMessage = ref(true);
function onFocus() {
    clicked.value = false;
    resetInputMode.value = false;
    isFocused.value = true;
    showValidationMessage.value = true;
    emit('focus');
}

async function onBlur() {
    isFocused.value = false;
    emit('blur');
    await sleep(1000);
    showValidationMessage.value = false;
}
</script>

<template>
    <div :class="[$style.text_input, minimizeInput ? $style.minimize_input : '', large ? $style.large : '', validationMessage ? $style.invalid : '', hidden ? $style.hidden : '']">
        <template v-if="createForm">
            <form
                :id="formName"
                :class="$style.form"
                :autocomplete="autocomplete ? 'autocomplete' : 'off'"
                @submit="onSubmit"
            />
            <input type="submit" :form="formName" hidden @click.stop/>
        </template>
        <InputLabel v-if="label && !hidden" :label="label" :no-label-top-gap="noLabelTopGap"/>
        <HorizontalAlignChildrenWrapper grow-children :class="$style.submit_button__wrapper" :wrap-on-small-screen="false">
            <div :class="$style.input__wrapper">
                <input
                    v-if="!hideInput"
                    :ref="setInputRef"
                    :placeholder="placeholder"
                    :type="type"
                    :form="formName"
                    :value="setNoInitialValue ? undefined : modelValue"
                    :name="name"
                    :autocomplete="autocomplete ? 'on' : 'off'"
                    :inputmode="inputMode"
                    :disabled="disabled"
                    :tabindex="tabindex"
                    :class="[
                        $style.input,
                        !disableInputGrowingByHiddenPlaceholder ? $style.input_placement : '',
                        bordersConfig.top ? $style.border_top : '',
                        bordersConfig.right ? $style.border_right : '',
                        bordersConfig.bottom ? $style.border_bottom : '',
                        bordersConfig.left ? $style.border_left : '',
                        focusBordersConfig.top ? $style.border_top_focus : '',
                        focusBordersConfig.right ? $style.border_right_focus : '',
                        focusBordersConfig.bottom ? $style.border_bottom_focus : '',
                        focusBordersConfig.left ? $style.border_left_focus : '',
                        whiteFill ? $style.white_fill : '',
                        whiteTextColor ? $style.white_text_color : '',
                        centerValue ? $style.center_value : '',
                        invisibleDisabledMode ? $style.invisible_disabled : '',
                    ]"
                    v-on="{ [event]: onChange }"
                    @click="clicked = true"
                    @focus="onFocus"
                    @blur="onBlur"
                    @keydown.esc="blur"
                />
                <p v-if="!disableInputGrowingByHiddenPlaceholder" :class="[$style.input, $style.placeholder_hidden]">{{ modelValue }}</p>
            </div>
            <Button
                v-if="submitButton"
                context="primary"
                :disabled="disabled"
                :class="$style.submit_button"
                :form="formName"
                type="submit"
            >
                {{ submitButton }}
            </Button>
        </HorizontalAlignChildrenWrapper>
        <FormError v-if="showValidationMessage && validationMessage" :message="validationMessage"/>
    </div>
</template>

<style lang="scss" module>
@use '@variables' as *;
@use '@sizes' as *;
@use '@borders' as *;

.text_input {
    width: $input-width;
    max-width: 100%;
    height: inherit;
}
.form {
    display: none;
}
.submit_button__wrapper {
    height: 100%;

    .submit_button {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }
}
.input__wrapper {
    position: relative;
    flex-grow: 1;
    height: 100%;
    overflow: hidden;

    .input_placement {
        position: absolute;
        top: 0;
        left: 0;
    }
    .placeholder_hidden {
        white-space: nowrap;
        visibility: hidden;
    }
}
.input {
    width: $input-width;
    min-width: $input-min-width;
    height: 100%;
    min-height: $input-min-height;
    padding: $input-padding-vertical $input-padding-horizontal;
    font-size: $font-size-0;
    color: $input-font-color;
    text-overflow: ellipsis;
    background-color: $input-background-color;
    border: none;
    border-radius: $input-border-radius;

    &.white_fill {
        background-color: #fff;
    }
    &.white_text_color {
        color: #fff;
    }
    &.center_value {
        text-align: center;
    }
    &:disabled:not(.invisible_disabled) {
        cursor: no-drop;
        background-color: $input-background-color-disabled;
        border: $input-border-hidden;
    }
    &:focus {
        outline: none;

        &.border_top_focus {
            border-top: $input-border-focus
        }
        &.border_right_focus {
            border-right: $input-border-focus
        }
        &.border_bottom_focus {
            border-bottom: $input-border-focus
        }
        &.border_left_focus {
            border-left: $input-border-focus
        }
    }
    &.border_top {
        border-top: $input-border;
    }
    &.border_right {
        border-right: $input-border;
    }
    &.border_bottom {
        border-bottom: $input-border;
    }
    &.border_left {
        border-left: $input-border;
    }
}
.invalid .input {
    border-color: $red-line-color;
}
.large {
    .input {
        min-height: $input-large-min-height;
        padding: $input-large-padding;
        font-size: $font-size-1;
    }
    .placeholder_hidden {
        padding: calc( #{$input-large-padding} * 1.2) $input-large-padding;
    }
}
.minimize_input .input {
    min-width: initial;
}
.hidden {
    width: 0;
    height: 0;
    margin: 0;
}
</style>
